import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@wlb/shared/context/AuthContext';
import { AdminSessionProvider } from '@wlb/shared/context/AdminSessionContext';
import { ThemeProvider } from '@wlb/shared/theme';
import { AdminShell } from './components/layout/AdminShell';
import { ThemeApplier } from './components/layout/ThemeApplier';
import { renderAppErrorFallback } from './components/feedback/AppErrorFallback';
import { SessionExpiredModal } from './components/feedback/SessionExpiredModal';
import { Toast } from './components/feedback/Toast';
import { StateBlock } from './components/ui/StateBlock';
import { ErrorBoundary } from './ErrorBoundary';
import { AdminLogin } from './features/auth/AdminLogin';
import { AnonymousOnly, RequireAdmin } from './features/auth/RequireAdmin';

const AdminAccounts = lazy(() =>
  import('./pages/AdminAccounts').then(module => ({
    default: module.AdminAccounts,
  })),
);
const AgentDetails = lazy(() =>
  import('./pages/AgentDetails').then(module => ({
    default: module.AgentDetails,
  })),
);
const Agents = lazy(() =>
  import('./pages/Agents').then(module => ({ default: module.Agents })),
);
const Announcements = lazy(() =>
  import('./pages/Announcements').then(module => ({
    default: module.Announcements,
  })),
);
const ContentModerationQueue = lazy(() =>
  import('./pages/ContentModerationQueue').then(module => ({
    default: module.ContentModerationQueue,
  })),
);
const ContentModerationSettings = lazy(() =>
  import('./pages/ContentModerationSettings').then(module => ({
    default: module.ContentModerationSettings,
  })),
);
const ContentModerationUsage = lazy(() =>
  import('./pages/ContentModerationUsage').then(module => ({
    default: module.ContentModerationUsage,
  })),
);
const Dashboard = lazy(() =>
  import('./pages/Dashboard').then(module => ({ default: module.Dashboard })),
);
const ErrorLogs = lazy(() =>
  import('./pages/ErrorLogs').then(module => ({ default: module.ErrorLogs })),
);
const Groups = lazy(() =>
  import('./pages/Groups').then(module => ({ default: module.Groups })),
);
const Affiliates = lazy(() =>
  import('./pages/Affiliates').then(module => ({ default: module.Affiliates })),
);
const AffiliateQualificationRule = lazy(() =>
  import('./pages/AffiliateQualificationRule').then(module => ({
    default: module.AffiliateQualificationRule,
  })),
);
const Moderation = lazy(() =>
  import('./pages/Moderation').then(module => ({ default: module.Moderation })),
);
const Posts = lazy(() =>
  import('./pages/Posts').then(module => ({ default: module.Posts })),
);
const Settings = lazy(() =>
  import('./pages/Settings').then(module => ({ default: module.Settings })),
);
const Statistics = lazy(() =>
  import('./pages/Statistics').then(module => ({ default: module.Statistics })),
);
const UserDetails = lazy(() =>
  import('./pages/UserDetails').then(module => ({
    default: module.UserDetails,
  })),
);
const Users = lazy(() =>
  import('./pages/Users').then(module => ({ default: module.Users })),
);
const AgentEditTab = lazy(() =>
  import('./features/agents/AgentEditTab').then(module => ({
    default: module.AgentEditTab,
  })),
);
const AgentProfileTab = lazy(() =>
  import('./features/agents/AgentProfileTab').then(module => ({
    default: module.AgentProfileTab,
  })),
);
const UserActivationTab = lazy(() =>
  import('./features/users/UserActivationTab').then(module => ({
    default: module.UserActivationTab,
  })),
);
const UserLoginsTab = lazy(() =>
  import('./features/users/UserLoginsTab').then(module => ({
    default: module.UserLoginsTab,
  })),
);
const UserPostsTab = lazy(() =>
  import('./features/users/UserPostsTab').then(module => ({
    default: module.UserPostsTab,
  })),
);
const UserProfileTab = lazy(() =>
  import('./features/users/UserProfileTab').then(module => ({
    default: module.UserProfileTab,
  })),
);

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminSessionProvider>
          <BrowserRouter>
            <ThemeApplier />
            <ErrorBoundary fallback={renderAppErrorFallback}>
              <Suspense fallback={<StateBlock status="loading" />}>
                <Routes>
                  <Route
                    path="/login"
                    element={
                      <AnonymousOnly>
                        <AdminLogin />
                      </AnonymousOnly>
                    }
                  />
                  {/* Every panel route sits inside RequireAdmin, so deep links to
                    a child route are guarded exactly like their parent. */}
                  <Route element={<RequireAdmin />}>
                    <Route element={<AdminShell />}>
                      <Route index element={<Dashboard />} />
                      <Route path="statistics" element={<Statistics />} />
                      <Route path="users" element={<Users />} />
                      {/* Each member sub-module is its own URL under the record's
                        layout route: the record is fetched once by the parent,
                        so switching sub-module swaps only the panel and a deep
                        link still lands on the right tab. */}
                      <Route path="users/:userGuid" element={<UserDetails />}>
                        <Route index element={<UserProfileTab />} />
                        <Route path="posts" element={<UserPostsTab />} />
                        <Route path="logins" element={<UserLoginsTab />} />
                        <Route
                          path="activation"
                          element={<UserActivationTab />}
                        />
                      </Route>
                      <Route path="agents" element={<Agents />} />
                      <Route path="agents/:agentId" element={<AgentDetails />}>
                        <Route index element={<AgentProfileTab />} />
                        <Route path="edit" element={<AgentEditTab />} />
                      </Route>
                      <Route path="posts" element={<Posts />} />
                      <Route path="groups" element={<Groups />} />
                      <Route path="affiliates" element={<Affiliates />} />
                      <Route
                        path="affiliates/qualification-rule"
                        element={<AffiliateQualificationRule />}
                      />
                      <Route
                        path="moderation"
                        element={<Navigate to="/moderation/pending" replace />}
                      />
                      <Route
                        path="moderation/pending"
                        element={<Moderation queue="pending" />}
                      />
                      <Route
                        path="moderation/reviewed"
                        element={<Moderation queue="reviewed" />}
                      />
                      <Route
                        path="content-moderation"
                        element={
                          <Navigate to="/content-moderation/queue" replace />
                        }
                      />
                      <Route
                        path="content-moderation/queue"
                        element={<ContentModerationQueue />}
                      />
                      <Route
                        path="content-moderation/controls"
                        element={<ContentModerationSettings />}
                      />
                      <Route
                        path="content-moderation/usage"
                        element={<ContentModerationUsage />}
                      />
                      <Route path="error-logs" element={<ErrorLogs />} />
                      <Route
                        path="admin-accounts"
                        element={<AdminAccounts />}
                      />
                      <Route path="announcements" element={<Announcements />} />
                      <Route path="settings" element={<Settings />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
              <SessionExpiredModal />
              <Toast />
            </ErrorBoundary>
          </BrowserRouter>
        </AdminSessionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
